import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Scan, FileText, FileCheck, X, BarChart3, AlertTriangle, CheckCircle,
  XCircle, Upload, Shield, ChevronRight, Loader2, Edit, Lock, Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_STEPS = [
  { id: "idle", label: "Pronto", icon: Upload },
  { id: "ingestion", label: "Ingestion", desc: "Upload & Pre-processing" },
  { id: "mapping", label: "Mapping AI", desc: "Estrazione con Gemini" },
  { id: "validation", label: "Validazione", desc: "Confidence & Check" },
  { id: "complete", label: "Completato", icon: CheckCircle },
];

function getConfidenceColor(confidence) {
  if (confidence >= 80) return { bg: "bg-emerald-50", border: "border-emerald-400", text: "text-emerald-700", dot: "bg-emerald-500", label: "Certo" };
  if (confidence >= 50) return { bg: "bg-amber-50", border: "border-amber-400", text: "text-amber-700", dot: "bg-amber-500", label: "Incerto" };
  return { bg: "bg-red-50", border: "border-red-400", text: "text-red-700", dot: "bg-red-500", label: "Basso" };
}

function ConfidenceBadge({ confidence }) {
  const c = getConfidenceColor(confidence);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {confidence}%
    </span>
  );
}

function FieldCard({ label, field, isEditing, onEdit, isMandatory }) {
  if (!field) return null;
  const val = field.value;
  const conf = field.confidence || 0;
  const c = getConfidenceColor(conf);
  const displayVal = val === null || val === undefined || val === "" ? "Non rilevato" : String(val);
  const isEmpty = val === null || val === undefined || val === "";

  return (
    <div
      data-testid={`ocr-field-${label.toLowerCase().replace(/[\s\/]/g, '-')}`}
      className={`p-3 rounded-lg border-2 transition-all ${c.border} ${c.bg} ${isEditing ? 'ring-2 ring-blue-400' : ''}`}
    >
      <div className="flex items-center justify-between mb-1">
        <Label className={`text-[11px] font-semibold uppercase tracking-wide ${c.text}`}>
          {isMandatory && <Lock className="w-3 h-3 inline mr-1" />}
          {label}
        </Label>
        <ConfidenceBadge confidence={conf} />
      </div>
      {isEditing ? (
        <Input
          className="h-8 text-sm font-mono mt-1"
          defaultValue={displayVal === "Non rilevato" ? "" : displayVal}
          onBlur={(e) => onEdit(e.target.value)}
          autoFocus
        />
      ) : (
        <p className={`font-bold text-sm ${isEmpty ? 'text-gray-400 italic' : ''}`}>{displayVal}</p>
      )}
    </div>
  );
}

function PipelineProgress({ currentStep, steps }) {
  const currentIdx = steps.findIndex(s => s.id === currentStep);
  return (
    <div className="flex items-center w-full gap-1 mb-6" data-testid="ocr-pipeline-progress">
      {steps.map((step, idx) => {
        const isActive = idx === currentIdx;
        const isDone = idx < currentIdx;
        const isPending = idx > currentIdx;
        return (
          <div key={step.id} className="flex items-center flex-1">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all w-full
              ${isActive ? 'bg-purple-600 text-white shadow-lg shadow-purple-200' : ''}
              ${isDone ? 'bg-emerald-100 text-emerald-700' : ''}
              ${isPending ? 'bg-gray-100 text-gray-400' : ''}
            `}>
              {isDone && <CheckCircle className="w-3.5 h-3.5 shrink-0" />}
              {isActive && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />}
              <span className="truncate">{step.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <ChevronRight className={`w-4 h-4 shrink-0 mx-0.5 ${isDone ? 'text-emerald-500' : 'text-gray-300'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function SmartOcrPanel({ onResultReady }) {
  const [file, setFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pipelineStep, setPipelineStep] = useState("idle");
  const [result, setResult] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setPdfUrl(URL.createObjectURL(f));
    setResult(null);
    setPipelineStep("idle");
  }, []);

  const handleExtract = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);

    // Step 1: Ingestion
    setPipelineStep("ingestion");
    await new Promise(r => setTimeout(r, 600));

    // Step 2: Mapping AI
    setPipelineStep("mapping");

    try {
      const fd = new FormData();
      fd.append("file", file);
      const response = await fetch(`${API}/admin/smart-ocr`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Errore OCR");
      }
      const data = await response.json();

      // Step 3: Validation
      setPipelineStep("validation");
      await new Promise(r => setTimeout(r, 500));

      if (data.status === "error") {
        toast.error(data.message || "Errore durante estrazione");
        setPipelineStep("idle");
        setLoading(false);
        return;
      }

      setPipelineStep("complete");
      setResult(data);
      if (onResultReady) onResultReady(data);

      if (data.validation?.is_valid) {
        toast.success(`Estrazione completata - Confidenza: ${data.overall_confidence}%`);
      } else {
        toast.warning("Estrazione completata con errori. Controlla i campi evidenziati.");
      }
    } catch (error) {
      toast.error(error.message || "Errore durante estrazione OCR");
      setPipelineStep("idle");
    } finally {
      setLoading(false);
    }
  }, [file, onResultReady]);

  const handleReset = () => {
    setFile(null);
    setPdfUrl(null);
    setResult(null);
    setPipelineStep("idle");
    setEditingField(null);
  };

  const handleFieldEdit = (section, fieldKey, newValue) => {
    if (!result) return;
    const updated = { ...result };
    if (updated.fields?.[section]?.[fieldKey]) {
      updated.fields[section][fieldKey].value = newValue;
      updated.fields[section][fieldKey].confidence = 100; // Manual edit = 100% confidence
    }
    setResult(updated);
    setEditingField(null);
  };

  const fields = result?.fields || {};
  const validation = result?.validation || {};

  const renderFieldGroup = (title, sectionKey, fieldDefs) => {
    const section = fields[sectionKey] || {};
    return (
      <div className="space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 border-b pb-1">{title}</h4>
        <div className="grid grid-cols-2 gap-2">
          {fieldDefs.map(({ key, label, mandatory }) => (
            <FieldCard
              key={key}
              label={label}
              field={section[key]}
              isMandatory={mandatory}
              isEditing={editingField === `${sectionKey}.${key}`}
              onEdit={(val) => handleFieldEdit(sectionKey, key, val)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-3" data-testid="smart-ocr-title">
            <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-2 rounded-xl">
              <Scan className="w-5 h-5" />
            </span>
            Neural OCR & AI Analysis
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">Estrazione intelligente dati energetici con Gemini AI</p>
        </div>
        {result && (
          <Button variant="outline" size="sm" onClick={handleReset} data-testid="ocr-reset-btn">
            <X className="w-4 h-4 mr-1" /> Reset
          </Button>
        )}
      </div>

      {/* Pipeline Progress */}
      {(loading || result) && (
        <PipelineProgress currentStep={pipelineStep} steps={PIPELINE_STEPS} />
      )}

      {/* Validation Alerts */}
      <AnimatePresence>
        {result && validation.blocking_errors?.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded-xl" data-testid="ocr-blocking-errors"
          >
            <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-1">
              <XCircle className="w-4 h-4" /> Errori critici nei dati estratti
            </div>
            {validation.blocking_errors.map((err, i) => (
              <p key={i} className="text-red-600 text-xs ml-6">- {err}</p>
            ))}
          </motion.div>
        )}
        {result && validation.warnings?.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl" data-testid="ocr-warnings"
          >
            <div className="flex items-center gap-2 text-amber-700 font-bold text-sm mb-1">
              <AlertTriangle className="w-4 h-4" /> Attenzione - Conferma manuale richiesta
            </div>
            {validation.warnings.map((w, i) => (
              <p key={i} className="text-amber-600 text-xs ml-6">- {w}</p>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Dual View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ocr-dual-view">
        {/* LEFT: PDF Viewer / Upload */}
        <div className="space-y-3">
          {!file ? (
            <Card className="border-2 border-dashed border-gray-300 hover:border-purple-400 transition-all cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="ocr-upload-zone"
            >
              <CardContent className="flex flex-col items-center justify-center py-16">
                <input ref={fileInputRef} type="file" accept=".pdf" className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                />
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                  <Upload className="w-8 h-8 text-purple-500" />
                </div>
                <p className="font-semibold text-lg">Carica Bolletta PDF</p>
                <p className="text-muted-foreground text-sm mt-1">Formato Bolletta 2.0 ARERA</p>
                <p className="text-xs text-gray-400 mt-3">Drag & Drop o clicca per selezionare</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-purple-200 overflow-hidden">
              <CardHeader className="py-2 px-4 bg-purple-50 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700 truncate max-w-[200px]">{file.name}</span>
                  {result && <span className="text-xs text-gray-500">({result.page_count} pag.)</span>}
                </div>
                <div className="flex gap-2">
                  {!loading && !result && (
                    <Button size="sm" onClick={handleExtract} data-testid="ocr-extract-btn"
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-7 text-xs"
                    >
                      <Zap className="w-3 h-3 mr-1" /> Estrai con AI
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={handleReset} className="h-7">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </CardHeader>
              <div className="bg-gray-900" style={{ height: "calc(100vh - 380px)", minHeight: "400px" }} data-testid="ocr-pdf-viewer">
                {pdfUrl && (
                  <iframe
                    src={`${pdfUrl}#toolbar=1&navpanes=0`}
                    className="w-full h-full"
                    title="PDF Viewer"
                    style={{ border: "none" }}
                  />
                )}
              </div>
            </Card>
          )}
        </div>

        {/* RIGHT: Extracted Data Form */}
        <div className="space-y-3">
          {!result && !loading ? (
            <Card className="border-2 border-gray-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-gray-300" />
                </div>
                <p className="font-semibold text-gray-400">Dati Estratti</p>
                <p className="text-sm text-gray-300 mt-1">Carica un PDF e avvia l'estrazione AI</p>
              </CardContent>
            </Card>
          ) : loading ? (
            <Card className="border-2 border-purple-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 text-purple-500 animate-spin mb-4" />
                <p className="font-semibold text-purple-700">
                  {pipelineStep === "ingestion" && "Pre-processing del documento..."}
                  {pipelineStep === "mapping" && "Gemini AI sta analizzando la bolletta..."}
                  {pipelineStep === "validation" && "Validazione e controllo congruenza..."}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Attendere prego</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3 overflow-y-auto pr-1" style={{ maxHeight: "calc(100vh - 320px)" }} data-testid="ocr-results-panel">
              {/* Overall Confidence Bar */}
              <Card className="border-2 border-purple-200">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-600" /> Confidenza Globale
                    </span>
                    <span className={`text-lg font-black ${
                      result.overall_confidence >= 80 ? 'text-emerald-600' :
                      result.overall_confidence >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>{result.overall_confidence}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className={`h-2.5 rounded-full transition-all duration-1000 ${
                      result.overall_confidence >= 80 ? 'bg-emerald-500' :
                      result.overall_confidence >= 50 ? 'bg-amber-500' : 'bg-red-500'
                    }`} style={{ width: `${result.overall_confidence}%` }} />
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-gray-400">
                    <span>0%</span>
                    <div className="flex gap-4">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Certo (&ge;80%)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Incerto</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Basso (&lt;50%)</span>
                    </div>
                    <span>100%</span>
                  </div>
                </CardContent>
              </Card>

              {/* Dati Identificativi */}
              {renderFieldGroup("Dati Identificativi", "dati_identificativi", [
                { key: "tipo_fornitura", label: "Tipo Fornitura", mandatory: true },
                { key: "fornitore", label: "Fornitore" },
                { key: "pod_pdr", label: "POD / PDR", mandatory: true },
                { key: "codice_fiscale", label: "CF / P.IVA", mandatory: true },
                { key: "intestatario", label: "Intestatario", mandatory: true },
              ])}

              {/* Dati Tecnici */}
              {renderFieldGroup("Dati Tecnici", "dati_tecnici", [
                { key: "potenza_impegnata", label: "Potenza Impegnata (kW)" },
                { key: "potenza_disponibile", label: "Potenza Disponibile (kW)" },
                { key: "tipologia_uso", label: "Tipologia Uso", mandatory: true },
                { key: "indirizzo_fornitura", label: "Indirizzo Fornitura", mandatory: true },
                { key: "tensione", label: "Tensione" },
              ])}

              {/* Dati Consumo */}
              {renderFieldGroup("Dati di Consumo", "dati_consumo", [
                { key: "consumo_annuo_stimato", label: "Consumo Annuo Stimato" },
                { key: "consumo_periodo", label: "Consumo Periodo" },
                { key: "periodo_riferimento", label: "Periodo" },
                { key: "consumo_f1", label: "Fascia F1" },
                { key: "consumo_f2", label: "Fascia F2" },
                { key: "consumo_f3", label: "Fascia F3" },
                { key: "unita_misura", label: "Unita Misura" },
              ])}

              {/* Dati Economici */}
              {renderFieldGroup("Dati Economici", "dati_economici", [
                { key: "totale_bolletta", label: "Totale Bolletta", mandatory: true },
                { key: "spesa_materia_prima", label: "Spesa Materia Prima" },
                { key: "spesa_trasporto", label: "Trasporto & Gestione" },
                { key: "oneri_sistema", label: "Oneri di Sistema" },
                { key: "imposte", label: "Imposte" },
                { key: "pcv_quota_fissa", label: "PCV (Quota Fissa)" },
                { key: "prezzo_energia_kwh", label: "Prezzo Energia (unit.)" },
                { key: "accise", label: "Accise" },
              ])}

              {/* Actions */}
              <Card className="border-2 border-gray-200">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-bold text-sm text-emerald-700">Estrazione Completata</p>
                        <p className="text-[11px] text-gray-500">Confidenza: {result.overall_confidence}%</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-xs h-8"
                        data-testid="ocr-edit-fields-btn"
                        onClick={() => setEditingField(editingField ? null : "all")}
                      >
                        <Edit className="w-3 h-3 mr-1" /> {editingField ? "Fine Modifica" : "Modifica Campi"}
                      </Button>
                      <Button size="sm" className="text-xs h-8"
                        data-testid="ocr-to-comparison-btn"
                        onClick={() => {
                          if (onResultReady) onResultReady(result);
                          toast.info("Passa alla sezione Comparazione per il confronto offerte");
                        }}
                      >
                        <BarChart3 className="w-3 h-3 mr-1" /> Comparazione
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
